import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import TableColumn from "../../components/TableColumn/TableColumn";
import Button from "../../components/Buttons/Button";
import api from "../../services/api";
import { PUSH_LIMITS, charCount, countState } from "../../utils/pushLimits";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import "./ManualPushes.scss";

const ManualPushes = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [campaigns, setCampaigns] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [tokenHealth, setTokenHealth] = useState(null);
  const [currentPage, setCurrentPage] = useState(
    Number(localStorage.getItem("ListCurrentPageManualPushes")) || 1
  );

  const records = 10;

  // Compose form state.
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [eventId, setEventId] = useState("");
  // A plan push opens that plan in the app; the Plans page links here with ?plan_id=.
  const [planId, setPlanId] = useState(searchParams.get("plan_id") || "");
  // The targeted plan's name and city, for the confirm text. null until looked up.
  const [planInfo, setPlanInfo] = useState(null);

  useEffect(() => {
    const id = planId.trim();
    setPlanInfo(null);
    if (!/^[a-f0-9]{24}$/i.test(id)) return undefined;
    let cancelled = false;
    api
      .get(`/user-guest-lists/${id}`)
      .then((r) => {
        const p = r.data?.data;
        if (!cancelled && p) setPlanInfo({ name: p.name, city: p.market || "miami" });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [planId]);
  const [link, setLink] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);

  // Scheduling state.
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduledFor, setScheduledFor] = useState(""); // datetime-local string
  const [scheduled, setScheduled] = useState([]); // pending queue
  const [cancelingId, setCancelingId] = useState(null);

  const formatDate = (timestamp) => {
    if (!timestamp) return "—";
    return format(new Date(Number(timestamp)), "MMM d, yyyy h:mm a");
  };

  // Earliest selectable time = one minute from now, in the admin's local time.
  const minDateTime = format(
    new Date(Date.now() + 60 * 1000),
    "yyyy-MM-dd'T'HH:mm"
  );

  const getCampaigns = useCallback(async (page) => {
    try {
      const response = await api.get(`/push-notification-admin/campaigns`, {
        params: {
          type: "manual",
          page,
          records,
        },
      });

      const items = response.data.data.map((c) => ({
        Title: c.title || "—",
        Message:
          c.message && c.message.length > 60
            ? c.message.substring(0, 60) + "..."
            : c.message || "—",
        "Date Sent": formatDate(c.sent_at),
        Sent: c.total_recipients,
        Opened:
          c.total_opened > 0 ? (
            <span
              style={{ cursor: "pointer", textDecoration: "underline", color: "#A89AFF" }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  `/push-opens?group_key=${encodeURIComponent(c.group_key)}&title=${encodeURIComponent(c.title || "")}`
                );
              }}
            >
              {c.total_opened}
            </span>
          ) : (
            c.total_opened
          ),
        "Open %": (
          <span
            className={`open-rate ${
              c.open_rate >= 50 ? "high" : c.open_rate >= 20 ? "medium" : "low"
            }`}
          >
            {c.open_rate}%
          </span>
        ),
        id: c.group_key,
      }));

      setCampaigns(items);
      setTotalPages(response.data.total);
    } catch (error) {
      console.error("Error fetching manual push campaigns:", error);
    }
  }, [navigate]);

  const getScheduled = useCallback(async () => {
    try {
      const response = await api.get(`/push-notification-admin/scheduled`, {
        params: { status: "pending" },
      });
      setScheduled(response.data.data || []);
    } catch (error) {
      console.error("Error fetching scheduled pushes:", error);
    }
  }, []);

  const getTokenHealth = useCallback(async () => {
    try {
      const response = await api.get(`/push-notification-admin/token-health`);
      setTokenHealth(response.data);
    } catch (error) {
      console.error("Error fetching token health:", error);
    }
  }, []);

  useEffect(() => {
    getCampaigns(currentPage);
  }, [currentPage, getCampaigns]);

  useEffect(() => {
    getScheduled();
  }, [getScheduled]);

  useEffect(() => {
    getTokenHealth();
  }, [getTokenHealth]);

  useEffect(() => {
    const storedPage = localStorage.getItem("ListCurrentPageManualPushes");
    setCurrentPage(Number(storedPage) || 1);
  }, []);

  const fetchAllData = async () => {
    try {
      const response = await api.get(`/push-notification-admin/campaigns`, {
        params: { type: "manual", records: 9999999999 },
      });

      return response.data.data.map((c) => ({
        Title: c.title || "—",
        Message: c.message || "—",
        "Date Sent": formatDate(c.sent_at),
        Sent: c.total_recipients,
        Opened: c.total_opened,
        "Open %": `${c.open_rate}%`,
        id: c.group_key,
      }));
    } catch (error) {
      console.error("Error fetching all data:", error);
      return [];
    }
  };

  const columns = ["Title", "Message", "Date Sent", "Sent", "Opened", "Open %"];

  const actions = [
    {
      label: <MdOutlineRemoveRedEye size={30} color="#4F30EA" />,
      onClick: (rowData) => {
        navigate(`/push-notifications/${encodeURIComponent(rowData.id)}`);
      },
    },
  ];

  const handlePageChange = (page) => {
    setCurrentPage(page);
    localStorage.setItem("ListCurrentPageManualPushes", page);
  };

  const resetForm = () => {
    setTitle("");
    setMessage("");
    setEventId("");
    setPlanId("");
    setLink("");
    // Drop ?plan_id so a reload doesn't target the same plan again.
    if (searchParams.has("plan_id")) {
      const next = new URLSearchParams(searchParams);
      next.delete("plan_id");
      setSearchParams(next, { replace: true });
    }
    setScheduleMode(false);
    setScheduledFor("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.warn("Message is required");
      return;
    }
    if (scheduleMode) {
      if (!scheduledFor) {
        toast.warn("Pick a date & time to schedule");
        return;
      }
      if (new Date(scheduledFor).getTime() <= Date.now()) {
        toast.warn("Scheduled time must be in the future");
        return;
      }
    }
    setConfirming(true);
  };

  const handleConfirmSend = async () => {
    setSending(true);
    try {
      const payload = {
        title: title.trim() || undefined,
        message: message.trim(),
        event_id: eventId.trim() || undefined,
        plan_id: planId.trim() || undefined,
        link: link.trim() || undefined,
      };

      if (scheduleMode) {
        const when = new Date(scheduledFor).getTime();
        await api.post(`/push-notification-admin/schedule`, {
          ...payload,
          scheduled_for: when,
        });
        toast.success(`Push scheduled for ${formatDate(when)}`);
        resetForm();
        setConfirming(false);
        getScheduled();
      } else {
        const response = await api.post(
          `/push-notification-admin/manual-send`,
          payload
        );
        const { audience, sent, failed } = response.data;
        toast.success(
          `Push sent to ${sent} of ${audience} users${
            failed ? ` (${failed} failed)` : ""
          }`
        );
        resetForm();
        setConfirming(false);
        setCurrentPage(1);
        localStorage.setItem("ListCurrentPageManualPushes", 1);
        getCampaigns(1);
      }
    } catch (error) {
      console.error("Error sending manual push:", error);
      toast.error(
        error?.response?.data?.message || "Failed to send push notification"
      );
    } finally {
      setSending(false);
    }
  };

  const handleCancelScheduled = async (id) => {
    setCancelingId(id);
    try {
      await api.delete(`/push-notification-admin/scheduled/${id}`);
      toast.success("Scheduled push canceled");
      getScheduled();
    } catch (error) {
      console.error("Error canceling scheduled push:", error);
      toast.error(
        error?.response?.data?.message || "Failed to cancel scheduled push"
      );
    } finally {
      setCancelingId(null);
    }
  };

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content" style={{ padding: 24 }}>
          <section className="manual-pushes">
            <div className="manual-pushes__compose">
              <h2 className="page-title m-0">Send a Manual Push</h2>
              <form className="manual-pushes__form" onSubmit={handleSubmit}>
                <div className="manual-pushes__field">
                  <label htmlFor="mp-title">Title</label>
                  <input
                    id="mp-title"
                    type="text"
                    placeholder="ZONA"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                  <small className={`manual-pushes__count ${countState(charCount(title), PUSH_LIMITS.title, PUSH_LIMITS.titlePreferred)}`.trim()}>
                    {charCount(title)}/{PUSH_LIMITS.title} · {PUSH_LIMITS.titlePreferred} or fewer is best, longer gets cut off on the lock screen
                  </small>
                </div>

                <div className="manual-pushes__field">
                  <label htmlFor="mp-message">
                    Message <span className="manual-pushes__required">*</span>
                  </label>
                  <textarea
                    id="mp-message"
                    rows={4}
                    placeholder="What do you want to say?"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                  />
                  <small className="manual-pushes__helper">
                    use {"{firstName}"} to personalize
                  </small>
                </div>

                <div className="manual-pushes__field">
                  <label htmlFor="mp-event">Event ID</label>
                  <input
                    id="mp-event"
                    type="text"
                    placeholder="optional"
                    value={eventId}
                    onChange={(e) => setEventId(e.target.value)}
                    disabled={!!link.trim() || !!planId.trim()}
                  />
                  <small className="manual-pushes__helper">
                    links the push to an event page
                  </small>
                </div>

                <div className="manual-pushes__field">
                  <label htmlFor="mp-plan">Plan ID</label>
                  <input
                    id="mp-plan"
                    type="text"
                    placeholder="optional"
                    value={planId}
                    onChange={(e) => setPlanId(e.target.value)}
                    disabled={!!link.trim() || !!eventId.trim()}
                  />
                  <small className="manual-pushes__helper">
                    opens that plan. copy the id from the{" "}
                    <span
                      style={{ cursor: "pointer", textDecoration: "underline" }}
                      onClick={() => navigate("/plans")}
                    >
                      Plans page
                    </span>
                  </small>
                </div>

                <div className="manual-pushes__field">
                  <label htmlFor="mp-link">Link</label>
                  <input
                    id="mp-link"
                    type="text"
                    placeholder="optional"
                    value={link}
                    onChange={(e) => setLink(e.target.value)}
                    disabled={!!eventId.trim() || !!planId.trim()}
                  />
                  <small className="manual-pushes__helper">
                    in-app venue, profile or chat links only. any other link (Plans tab included) opens Home
                  </small>
                </div>

                <div className="manual-pushes__schedule-toggle">
                  <label>
                    <input
                      type="checkbox"
                      checked={scheduleMode}
                      onChange={(e) => {
                        setScheduleMode(e.target.checked);
                        setConfirming(false);
                      }}
                    />
                    Schedule for later
                  </label>
                </div>

                {scheduleMode && (
                  <div className="manual-pushes__field">
                    <label htmlFor="mp-when">Send at</label>
                    <input
                      id="mp-when"
                      type="datetime-local"
                      value={scheduledFor}
                      min={minDateTime}
                      onChange={(e) => setScheduledFor(e.target.value)}
                    />
                    <small className="manual-pushes__helper">
                      your local time. fires within a minute of this time.
                    </small>
                  </div>
                )}

                {!confirming ? (
                  <Button
                    type="submit"
                    gradientButton
                    textColor="#FFFFFF"
                    disabled={!message.trim()}
                  >
                    {scheduleMode ? "Schedule Push" : "Send Push"}
                  </Button>
                ) : (
                  <div className="manual-pushes__confirm">
                    <p className="manual-pushes__confirm-warning">
                      {(() => {
                        // A plan push only goes to the plan's city (the server enforces it).
                        const who = planId.trim()
                          ? `users in ${planInfo?.city || "the plan's city"} with notifications on${
                              planInfo?.name ? `, opening "${planInfo.name}"` : ""
                            }`
                          : "ALL users with notifications on";
                        return scheduleMode
                          ? `This will send to ${who} at ${formatDate(new Date(scheduledFor).getTime())}.`
                          : `This sends a push to ${who}.`;
                      })()}
                    </p>
                    <div className="manual-pushes__confirm-actions">
                      <Button
                        type="button"
                        gradientButton
                        textColor="#FFFFFF"
                        onClick={handleConfirmSend}
                        disabled={sending}
                        showLoading={sending}
                      >
                        {scheduleMode ? "Confirm & Schedule" : "Confirm & Send"}
                      </Button>
                      <Button
                        type="button"
                        className="btn-outline-light"
                        onClick={() => setConfirming(false)}
                        disabled={sending}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </form>
            </div>

            <div className="manual-pushes__queue">
              <h3 className="manual-pushes__queue-title">
                Scheduled ({scheduled.length})
              </h3>
              {scheduled.length === 0 ? (
                <p className="manual-pushes__queue-empty">
                  No scheduled pushes. Turn on "Schedule for later" above to queue one.
                </p>
              ) : (
                <ul className="manual-pushes__queue-list">
                  {scheduled.map((s) => (
                    <li key={s._id} className="manual-pushes__queue-item">
                      <div className="manual-pushes__queue-when">
                        {formatDate(s.scheduled_for)}
                      </div>
                      <div className="manual-pushes__queue-body">
                        {s.title && (
                          <span className="manual-pushes__queue-name">
                            {s.title}
                          </span>
                        )}
                        <span className="manual-pushes__queue-msg">
                          {s.message}
                        </span>
                        {(s.event_id || s.plan_id || s.link) && (
                          <span className="manual-pushes__queue-target">
                            {s.event_id
                              ? `event: ${s.event_id}`
                              : s.plan_id
                                ? `plan: ${s.plan_id}`
                                : `link: ${s.link}`}
                          </span>
                        )}
                      </div>
                      <Button
                        type="button"
                        className="btn-outline-light"
                        onClick={() => handleCancelScheduled(s._id)}
                        disabled={cancelingId === s._id}
                        showLoading={cancelingId === s._id}
                      >
                        Cancel
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {tokenHealth && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 24,
                  alignItems: "center",
                  padding: "16px 20px",
                  marginBottom: 16,
                  borderRadius: 12,
                  background: "#0e0e0e",
                  border: "1px solid #1f1f1f",
                }}
              >
                <span style={{ fontWeight: 600, color: "#fff", fontSize: 15 }}>
                  Push reach
                </span>
                <div>
                  <div style={{ color: "#777", fontSize: 12 }}>Stored tokens</div>
                  <div style={{ color: "#fff", fontSize: 20, fontWeight: 600 }}>
                    {(tokenHealth.stored ?? 0).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div style={{ color: "#777", fontSize: 12 }}>Notifications on</div>
                  <div style={{ color: "#fff", fontSize: 20, fontWeight: 600 }}>
                    {(tokenHealth.eligible ?? 0).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div style={{ color: "#777", fontSize: 12 }}>
                    Reachable (can receive)
                  </div>
                  <div
                    style={{ color: "#3ECF8E", fontSize: 20, fontWeight: 600 }}
                  >
                    {tokenHealth.reachable == null
                      ? "—"
                      : tokenHealth.reachable.toLocaleString()}
                    {(() => {
                      const h = tokenHealth.history || [];
                      if (h.length < 2 || tokenHealth.reachable == null)
                        return null;
                      const prev = h[h.length - 2]?.reachable;
                      if (prev == null) return null;
                      const d = tokenHealth.reachable - prev;
                      if (d === 0) return null;
                      return (
                        <span
                          style={{
                            fontSize: 13,
                            marginLeft: 8,
                            color: d < 0 ? "#FF6B6B" : "#3ECF8E",
                          }}
                        >
                          {d < 0 ? "▼" : "▲"} {Math.abs(d).toLocaleString()}{" "}
                          since last check
                        </span>
                      );
                    })()}
                  </div>
                </div>
                <div>
                  <div style={{ color: "#777", fontSize: 12 }}>
                    Dead (won't receive)
                  </div>
                  <div
                    style={{ color: "#FF6B6B", fontSize: 20, fontWeight: 600 }}
                  >
                    {tokenHealth.dead == null
                      ? "—"
                      : tokenHealth.dead.toLocaleString()}
                  </div>
                </div>
                <span
                  style={{ marginLeft: "auto", color: "#777", fontSize: 12 }}
                >
                  {tokenHealth.checked_at
                    ? `reach checked ${formatDate(tokenHealth.checked_at)}`
                    : "reach check runs daily — pending first run"}
                </span>
              </div>
            )}

            <TableColumn
              title="Past Manual Pushes"
              hasHeader={true}
              columns={columns}
              data={campaigns}
              hasExport={true}
              actions={actions}
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              records={records}
              fetchAllData={fetchAllData}
            />
          </section>
        </article>
      </div>
    </main>
  );
};

export default ManualPushes;
